// src/redux/slices/mealOffSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../api/authApi';

// ========== ASYNC THUNKS ==========

// Fetch meal offs for a subscription
// In your mealOffSlice.js
export const fetchMealOffsAsync = createAsyncThunk(
  'mealOff/fetch',
  async (subscriptionId, { rejectWithValue }) => {
    try {
      // ✅ Use apiService which now uses POST
      const response = await apiService.getMealOffs(subscriptionId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);;

// Mark a meal as off (skip)
export const markMealOffAsync = createAsyncThunk(
  'mealOff/markMealOff',
  async ({ subscriptionId, date, meal_time, meal_type }, { rejectWithValue }) => {
    try {
      console.log('🔄 Marking meal off:', { subscriptionId, date, meal_time, meal_type });
      const response = await apiService.markMealOff(
        subscriptionId,
        date,
        meal_time,
        meal_type
      );
      
      if (response?.status) {
        console.log('✅ Meal marked off:', response.data);
        return {
          subscriptionId,
          date,
          meal_time,
          meal_type,
          data: response.data
        };
      }
      return rejectWithValue(response?.message || 'Failed to mark meal off');
    } catch (error) {
      console.error('❌ Failed to mark meal off:', error);
      return rejectWithValue(error.message || 'Failed to mark meal off');
    }
  }
);

// Remove a meal off (unskip)
export const removeMealOffAsync = createAsyncThunk(
  'mealOff/removeMealOff',
  async ({ subscriptionId, date, meal_time, meal_type }, { rejectWithValue }) => {
    try {
      console.log('🔄 Removing meal off:', { subscriptionId, date, meal_time, meal_type });
      const response = await apiService.removeMealOff(
        subscriptionId,
        date,
        meal_time,
        meal_type
      );
      
      if (response?.status) {
        console.log('✅ Meal off removed:', response.data);
        return {
          subscriptionId,
          date,
          meal_time,
          meal_type,
          data: response.data
        };
      }
      return rejectWithValue(response?.message || 'Failed to remove meal off');
    } catch (error) {
      console.error('❌ Failed to remove meal off:', error);
      return rejectWithValue(error.message || 'Failed to remove meal off');
    }
  }
);

// ========== INITIAL STATE ==========

const initialState = {
  // Map of subscriptionId -> mealOffs array
  mealOffsBySubscription: {},
  // Loading states
  loading: false,
  fetching: false,
  error: null,
  // Current operation status
  operationStatus: 'idle', // 'idle' | 'loading' | 'success' | 'error'
  operationMessage: null,
  // Last updated subscription
  lastUpdatedSubscriptionId: null,
};

// ========== SLICE ==========

const mealOffSlice = createSlice({
  name: 'mealOff',
  initialState,
  reducers: {
    // Clear meal offs for a subscription
    clearMealOffs: (state, action) => {
      const subscriptionId = action.payload;
      if (subscriptionId) {
        delete state.mealOffsBySubscription[subscriptionId];
      }
      state.error = null;
    },
    // Clear all meal offs
    clearAllMealOffs: (state) => {
      state.mealOffsBySubscription = {};
      state.error = null;
    },
    // Reset operation status
    resetOperationStatus: (state) => {
      state.operationStatus = 'idle';
      state.operationMessage = null;
      state.error = null;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== FETCH MEAL OFFS =====
      .addCase(fetchMealOffsAsync.pending, (state) => {
        state.fetching = true;
        state.error = null;
      })
      .addCase(fetchMealOffsAsync.fulfilled, (state, action) => {
        state.fetching = false;
        const { subscriptionId, mealOffs } = action.payload;
        state.mealOffsBySubscription[subscriptionId] = mealOffs;
        state.lastUpdatedSubscriptionId = subscriptionId;
        state.error = null;
      })
      .addCase(fetchMealOffsAsync.rejected, (state, action) => {
        state.fetching = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch meal offs';
      })

      // ===== MARK MEAL OFF =====
      .addCase(markMealOffAsync.pending, (state) => {
        state.loading = true;
        state.operationStatus = 'loading';
        state.operationMessage = 'Skipping meal...';
        state.error = null;
      })
      .addCase(markMealOffAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.operationStatus = 'success';
        state.operationMessage = 'Meal skipped successfully!';
        
        const { subscriptionId, date, meal_time, meal_type, data } = action.payload;
        state.lastUpdatedSubscriptionId = subscriptionId;
        
        // Update meal offs for the subscription
        const mealOffsData = data?.meal_offs || [];
        if (mealOffsData.length > 0) {
          state.mealOffsBySubscription[subscriptionId] = mealOffsData;
        } else {
          // If no meal offs data returned, update the specific meal
          const currentMealOffs = state.mealOffsBySubscription[subscriptionId] || [];
          // Check if this meal is already in the list
          const exists = currentMealOffs.some(
            off => off.date === date && off.meal_time === meal_time && off.meal_type === meal_type
          );
          if (!exists) {
            state.mealOffsBySubscription[subscriptionId] = [
              ...currentMealOffs,
              { date, meal_time, meal_type }
            ];
          }
        }
        
        state.error = null;
      })
      .addCase(markMealOffAsync.rejected, (state, action) => {
        state.loading = false;
        state.operationStatus = 'error';
        state.operationMessage = 'Failed to skip meal';
        state.error = action.payload || action.error?.message || 'Failed to mark meal off';
      })

      // ===== REMOVE MEAL OFF =====
      .addCase(removeMealOffAsync.pending, (state) => {
        state.loading = true;
        state.operationStatus = 'loading';
        state.operationMessage = 'Removing skipped meal...';
        state.error = null;
      })
      .addCase(removeMealOffAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.operationStatus = 'success';
        state.operationMessage = 'Meal off removed successfully!';
        
        const { subscriptionId, date, meal_time, meal_type, data } = action.payload;
        state.lastUpdatedSubscriptionId = subscriptionId;
        
        // Update meal offs for the subscription
        const mealOffsData = data?.meal_offs || [];
        if (mealOffsData.length > 0) {
          state.mealOffsBySubscription[subscriptionId] = mealOffsData;
        } else {
          // Remove the specific meal from the list
          const currentMealOffs = state.mealOffsBySubscription[subscriptionId] || [];
          state.mealOffsBySubscription[subscriptionId] = currentMealOffs.filter(
            off => !(off.date === date && off.meal_time === meal_time && off.meal_type === meal_type)
          );
        }
        
        state.error = null;
      })
      .addCase(removeMealOffAsync.rejected, (state, action) => {
        state.loading = false;
        state.operationStatus = 'error';
        state.operationMessage = 'Failed to remove skipped meal';
        state.error = action.payload || action.error?.message || 'Failed to remove meal off';
      });
  },
});

// ========== SELECTORS ==========

// Get meal offs for a specific subscription
export const selectMealOffsBySubscription = (state) => state.mealOff.mealOffsBySubscription;
export const selectMealOffsForSubscription = (subscriptionId) => (state) => 
  state.mealOff.mealOffsBySubscription[subscriptionId] || [];

// Get loading states
export const selectMealOffLoading = (state) => state.mealOff.loading;
export const selectMealOffFetching = (state) => state.mealOff.fetching;
export const selectMealOffError = (state) => state.mealOff.error;
export const selectMealOffOperationStatus = (state) => state.mealOff.operationStatus;
export const selectMealOffOperationMessage = (state) => state.mealOff.operationMessage;
export const selectLastUpdatedSubscriptionId = (state) => state.mealOff.lastUpdatedSubscriptionId;

// Check if a specific meal is skipped
export const selectIsMealSkipped = (subscriptionId, date, mealTime, mealType) => (state) => {
  const mealOffs = state.mealOff.mealOffsBySubscription[subscriptionId] || [];
  return mealOffs.some(off => {
    const offDate = off.date || off.Date;
    const offMealTime = off.meal_time || off.mealTime || off.MealTime;
    const offMealType = off.meal_type || off.mealType || off.MealType;
    
    let typeMatches = false;
    if (Array.isArray(offMealType)) {
      typeMatches = offMealType.some(t => t.toLowerCase() === mealType?.toLowerCase());
    } else if (typeof offMealType === 'string') {
      typeMatches = offMealType.toLowerCase() === mealType?.toLowerCase();
    }
    
    return offDate === date && offMealTime === mealTime && typeMatches;
  });
};

// Get all skipped meals for a date
export const selectSkippedMealsForDate = (subscriptionId, dateStr) => (state) => {
  const mealOffs = state.mealOff.mealOffsBySubscription[subscriptionId] || [];
  return mealOffs.filter(off => {
    const offDate = off.date || off.Date;
    return offDate === dateStr;
  });
};

// Check if a date is fully locked (all 3 meals skipped)
export const selectIsDateFullyLocked = (subscriptionId, dateStr) => (state) => {
  const mealOffs = state.mealOff.mealOffsBySubscription[subscriptionId] || [];
  const skippedForDate = mealOffs.filter(off => {
    const offDate = off.date || off.Date;
    return offDate === dateStr;
  });
  const skippedMealTimes = skippedForDate.map(m => m.meal_time || m.MealTime);
  const allMeals = ['Breakfast', 'Lunch', 'Dinner'];
  const allSkipped = allMeals.every(meal => skippedMealTimes.includes(meal));
  return allSkipped && skippedForDate.length >= 3;
};

// Get available meal times for a date
export const selectAvailableMealTimes = (subscriptionId, dateStr) => (state) => {
  if (!dateStr) return ['Breakfast', 'Lunch', 'Dinner'];
  const mealOffs = state.mealOff.mealOffsBySubscription[subscriptionId] || [];
  const skippedForDate = mealOffs.filter(off => {
    const offDate = off.date || off.Date;
    return offDate === dateStr;
  });
  const skippedMealTimes = skippedForDate.map(m => m.meal_time || m.MealTime);
  return ['Breakfast', 'Lunch', 'Dinner'].filter(time => !skippedMealTimes.includes(time));
};

// ========== EXPORTS ==========

export const {
  clearMealOffs,
  clearAllMealOffs,
  resetOperationStatus,
  clearError,
} = mealOffSlice.actions;

export default mealOffSlice.reducer;